import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AsyncPipe, NgClass } from '@angular/common';

import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CmsImagePipe, CmsService, LocaleService, TranslatePipe } from '@selfcare/cms';
import { SvgComponent } from '@shared/components/svg/svg.component';
import { MsisdnPreviewComponent } from '@shared/components/msisdn-preview/msisdn-preview.component';
import { filter, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { userFeature } from '@state';
import { toSignal } from '@angular/core/rxjs-interop';
import { MobileProfileComponent } from '../../../shared/components/mobile-profile/mobile-profile.component';
import {
  getLinkNavigationList,
  linkNavigationList,
  linkNavigationListLimited,
} from '@core/const/const';
import { NavigationLink } from '@core/models';
import { ButtonComponent } from '@shared/components';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AppRouting } from '@core/enums';
import { isParentLinkMatch } from '@core/helpers';
import { generalFeature } from '@src/state/general';
import { ProductType } from '@core/contracts/data-contracts';

@UntilDestroy()
@Component({
  selector: 'sc-mobile-header',
  imports: [
    RouterLink,
    RouterLinkActive,
    AsyncPipe,
    TranslatePipe,
    FormsModule,
    ReactiveFormsModule,
    SvgComponent,
    CmsImagePipe,
    MsisdnPreviewComponent,
    MobileProfileComponent,
    ButtonComponent,
    CmsImagePipe,
    NgClass,
  ],
  templateUrl: './mobile-header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileHeaderComponent implements OnInit {
  @ViewChild('mobileProfile') profileCmp!: MobileProfileComponent;

  selectedPath: string = null;
  form = this.fb.group({
    languageCode: this.localeService.currentLocale,
  });
  public title = signal('');
  public linkList = signal(linkNavigationList);

  public isLimited = this.store.selectSignal(userFeature.selectIsMsisdnLimited);
  public isJottaInUse = this.store.selectSignal(userFeature.selectIsJottaInUse);
  public isTerminated = this.store.selectSignal(userFeature.selectIsTerminated);
  public currentUser = this.store.selectSignal(userFeature.selectCurrent);
  public isDataOnly = this.store.selectSignal(generalFeature.selectIsDataOnly);
  public isB2B = this.store.selectSignal(userFeature.selectIsB2B);
  public isPortingBlocked = this.store.selectSignal(userFeature.selectIsPortingBlocked);
  public activeProduct = this.store.selectSignal(userFeature.selectActiveProduct);

  public isInternet = computed(() => this.activeProduct() === ProductType.Internet);

  public hideNav = input<boolean>(false);

  public linkNavigationList = computed(() => {
    return getLinkNavigationList(
      this.isLimited(),
      this.isTerminated(),
      this.isJottaInUse(),
      this.isDataOnly(),
      this.isB2B(),
      this.isInternet()
    );
  });

  initials = this.store.selectSignal(userFeature.selectUserInitials);
  logoUrl = toSignal(this.cmsService.headerLogo$);
  isOpen = signal(false);
  isOpenProfile = signal(false);

  constructor(
    private readonly cmsService: CmsService,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly localeService: LocaleService,
    private readonly store: Store
  ) { }

  public ngOnInit() {
    this.openLinkGroup();

    this.store
      .select(userFeature.selectIsMsisdnLimited)
      .pipe(
        untilDestroyed(this),
        tap((value) => {
          if (value) {
            this.linkList.set(linkNavigationListLimited);
          }
        })
      )
      .subscribe();

    this.form.valueChanges
      .pipe(tap((value) => this.localeService.changeLocale(value.languageCode)))
      .subscribe();

    this.updateTitle(this.router.url);

    this.router.events
      .pipe(
        untilDestroyed(this),
        filter((e) => e instanceof NavigationEnd),
        tap((e: NavigationEnd) => {
          this.isOpen.set(false);
          this.updateTitle(e.urlAfterRedirects);
          this.openLinkGroup();
          document.querySelector('html').style.overflow = 'auto';
        })
      )
      .subscribe();
  }

  private updateTitle(url: string) {
    if (url.includes(AppRouting.Prolongation)) {
      this.title.set('navigation.subscription');
      return;
    }

    const allLinks = this.linkList().flatMap((link) => [
      { label: link.label, path: link.path },
      ...(link.subLinks?.map((subLink) => ({ label: subLink.label, path: subLink.path })) || []),
    ]);

    const match = allLinks.find((link) => link.path === url);
    if (match) {
      this.title.set(match.label);
    }
  }

  public isParentLinkActive(linkGroup: any): boolean {
    return isParentLinkMatch(this.router.url, linkGroup);
  }

  public setSelectedPath(path: string) {
    if (path === this.selectedPath) {
      this.selectedPath = null;
      return;
    }

    this.selectedPath = path;
  }

  public toggleMenu() {
    const isOpen = !this.isOpen();
    this.isOpen.set(isOpen);

    if (isOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
  }

  public toggle() {
    this.isOpenProfile.set(!this.isOpenProfile());
    if (this.isOpenProfile()) {
      setTimeout(() => {
        if (this.profileCmp?.msisdnListCmp) {
          this.profileCmp.msisdnListCmp.scrollToSelectedMsisdn();
        }
      });
    }
  }

  public closeProfileModal() {
    this.isOpenProfile.set(false);
  }

  public toggleSubLinks(linkGroup: NavigationLink): void {
    if (this.selectedPath === linkGroup.path) {
      this.selectedPath = null;
    } else {
      this.selectedPath = linkGroup.path;
    }
  }

  private openLinkGroup(): void {
    const routePaths = this.router.url.split('/').filter(Boolean);
    const isChildRoute = routePaths.length > 1;

    if (isChildRoute) {
      const linkList = this.linkList().map((linkGroup) => {
        const rootRouteUrl = routePaths[0];
        return {
          ...linkGroup,
          isOpen: linkGroup.path === rootRouteUrl,
        };
      });

      this.linkList.set(linkList);
    }
  }

  handleLogoRedirect() {
    this.currentUser
      ? this.router.navigate([AppRouting.Overview])
      : this.router.navigate([AppRouting.Login]);
  }
}
